import React, { useState, useEffect, useRef } from 'react';
import { Download, Trash2, FileText } from 'lucide-react';
import { assetTypes, departments } from '../../data/dummyData';
import { assetService } from '../../services/assetService';
import AssetMasterDataService from '../../services/assetMasterDataService';
import API_CONFIG from '../../config/api';

// Helper to get correct file URL
const getFileUrl = (url) => {
  if (!url) return '#';
  if (url.startsWith('http')) return url;
  const normalizedPath = url.replace(/\\/g, '/');
  const pathWithSlash = normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;
  return `${API_CONFIG.BASE_URL}${pathWithSlash}`;
};

// Comprehensive hardcoded subtypes for all asset types
const HARDCODED_SUBTYPES = {
  'Real Estate': [
    { code: 'COMMERCIAL_BUILDING', label: 'Commercial Building' },
    { code: 'WAREHOUSE', label: 'Warehouse' },
    { code: 'OFFICE_SPACE', label: 'Office Space' },
    { code: 'RETAIL_SPACE', label: 'Retail Space' },
    { code: 'INDUSTRIAL_FACILITY', label: 'Industrial Facility' },
    { code: 'RESIDENTIAL_PROPERTY', label: 'Residential Property' },
    { code: 'LAND', label: 'Land' },
    { code: 'BUILDING', label: 'Building' }
  ],
  'Vehicle': [
    { code: 'SEDAN', label: 'Sedan' },
    { code: 'COMMERCIAL_VEHICLE', label: 'Commercial Vehicle' },
    { code: 'TRUCK', label: 'Truck' },
    { code: 'VAN', label: 'Van' },
    { code: 'MOTORCYCLE', label: 'Motorcycle' },
    { code: 'SUV', label: 'SUV' },
    { code: 'FLEET_VEHICLE', label: 'Fleet Vehicle' }
  ],
  'Machinery': [
    { code: 'MANUFACTURING_EQUIPMENT', label: 'Manufacturing Equipment' },
    { code: 'PRODUCTION_EQUIPMENT', label: 'Production Equipment' },
    { code: 'PACKAGING_EQUIPMENT', label: 'Packaging Equipment' },
    { code: 'INDUSTRIAL_MACHINERY', label: 'Industrial Machinery' },
    { code: 'CNC_MACHINE', label: 'CNC Machine' }
  ],
  'IT Equipment': [
    { code: 'SERVER_HARDWARE', label: 'Server Hardware' },
    { code: 'COMPUTER_HARDWARE', label: 'Computer Hardware' },
    { code: 'NETWORK_EQUIPMENT', label: 'Network Equipment' },
    { code: 'PRINTER', label: 'Printer' },
    { code: 'SCANNER', label: 'Scanner' },
    { code: 'MONITOR', label: 'Monitor' },
    { code: 'LAPTOP', label: 'Laptop' },
    { code: 'DESKTOP', label: 'Desktop' }
  ],
  'Furniture': [
    { code: 'OFFICE_FURNITURE', label: 'Office Furniture' },
    { code: 'CONFERENCE_ROOM_FURNITURE', label: 'Conference Room Furniture' },
    { code: 'DESK', label: 'Desk' },
    { code: 'CHAIR', label: 'Chair' },
    { code: 'CABINET', label: 'Cabinet' },
    { code: 'SHELVING', label: 'Shelving' }
  ],
  'Laboratory Equipment': [
    { code: 'ANALYTICAL_EQUIPMENT', label: 'Analytical Equipment' },
    { code: 'TESTING_EQUIPMENT', label: 'Testing Equipment' },
    { code: 'MEASUREMENT_DEVICE', label: 'Measurement Device' }
  ],
  'Manufacturing Equipment': [
    { code: 'ASSEMBLY_LINE', label: 'Assembly Line' },
    { code: 'QUALITY_CONTROL', label: 'Quality Control Equipment' },
    { code: 'MATERIAL_HANDLING', label: 'Material Handling Equipment' }
  ],
  'Other': [
    { code: 'MISCELLANEOUS', label: 'Miscellaneous' },
    { code: 'TOOL', label: 'Tool' },
    { code: 'EQUIPMENT', label: 'General Equipment' }
  ]
};

const AssetForm = ({ asset, onSave, onCancel, testData = null, viewMode = false }) => {
  // Helper function to get form control props based on view mode
  const getFormControlProps = (type = 'text') => {
    const baseStyle = {
      borderRadius: '0',
      border: '1px solid #d1d5db',
      padding: '8px 12px',
      fontSize: '14px',
      width: '100%',
      boxSizing: 'border-box'
    };

    if (viewMode) {
      return {
        readOnly: true,
        disabled: true,
        className: 'form-control',
        style: {
          ...baseStyle,
          backgroundColor: '#f3f4f6',
          color: '#1f2937',
          cursor: 'not-allowed'
        }
      };
    }

    return {
      className: 'form-control',
      style: baseStyle
    };
  };

  // Helper function to safely parse vendor avoiding corrupted DB strings
  // Helper function to safely parse vendor avoiding corrupted DB strings
  let initialVendor = '';
  if (asset?.vendor) {
    if (typeof asset.vendor === 'string') {
      initialVendor = asset.vendor === '[object Object]' ? '' : asset.vendor;
    } else if (typeof asset.vendor === 'object') {
      initialVendor = asset.vendor.name && asset.vendor.name !== '[object Object]' ? asset.vendor.name : '';
    }
  } else if (testData?.vendor) {
    initialVendor = typeof testData.vendor === 'string' ? testData.vendor : (testData.vendor?.name || '');
  }

  const [formData, setFormData] = useState({
    assetCode: asset?.assetCode || testData?.assetCode || '',
    name: asset?.name || testData?.name || '',
    type: asset?.type || testData?.type || '',
    subtype: asset?.subtype || testData?.subtype || '',
    department: asset?.department || testData?.department || '',
    status: asset?.status || testData?.status || 'Active',
    purchaseDate: asset?.purchaseDate ? new Date(asset.purchaseDate).toISOString().split('T')[0] : testData?.purchaseDate || '',
    vendor: initialVendor,
    cost: asset?.cost || testData?.cost || '',
    depreciationMethod: asset?.depreciation?.method || asset?.depreciationMethod || testData?.depreciationMethod || 'Straight Line',
    location: {
      building: asset?.location?.building || testData?.location?.building || '',
      floor: asset?.location?.floor || testData?.location?.floor || '',
      room: asset?.location?.room || testData?.location?.room || '',
      address: asset?.location?.address || testData?.location?.address || '',
      coordinates: {
        latitude: asset?.location?.coordinates?.latitude || testData?.location?.coordinates?.latitude || '',
        longitude: asset?.location?.coordinates?.longitude || testData?.location?.coordinates?.longitude || ''
      }
    },
    assignedTo: asset?.assignedTo || testData?.assignedTo || ''
  });

  const [uploadedFile, setUploadedFile] = useState(null);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  // Master data state
  const [masterData, setMasterData] = useState({
    types: [],
    subtypes: [],
    departments: [],
    depreciationMethods: [],
    statuses: []
  });
  const [loadingMasterData, setLoadingMasterData] = useState(true);

  // Load master data on component mount
  useEffect(() => {
    loadMasterData();
  }, []);

  // Load subtypes when form data changes and type is set
  useEffect(() => {
    if (formData.type && masterData.types.length > 0) {
      loadSubtypes(formData.type);
    }
  }, [formData.type, masterData.types]);

  // Load existing documents when editing an asset
  useEffect(() => {
    if (asset?._id) {
      loadExistingDocuments();
    }
  }, [asset?._id]);

  // Use ref to track previous asset ID to prevent unnecessary re-initializations
  const previousAssetIdRef = useRef(null);
  const previousTestDataRef = useRef(null);

  // Update form data when test data changes (only when testData actually changes)
  useEffect(() => {
    if (testData && !asset) {
      // Only update if testData has actually changed
      let effectTestVendor = '';
      if (testData.vendor) {
        if (typeof testData.vendor === 'string') {
          effectTestVendor = testData.vendor === '[object Object]' ? '' : testData.vendor;
        } else if (typeof testData.vendor === 'object') {
          effectTestVendor = testData.vendor.name && testData.vendor.name !== '[object Object]' ? testData.vendor.name : '';
        }
      }

      if (previousTestDataRef.current !== testData) {
        setFormData({
          assetCode: testData.assetCode || '',
          name: testData.name || '',
          type: testData.type || '',
          subtype: testData.subtype || '',
          department: testData.department || '',
          status: testData.status || 'Active',
          purchaseDate: testData.purchaseDate || '',
          vendor: effectTestVendor,
          cost: testData.cost || '',
          depreciationMethod: testData.depreciationMethod || 'Straight Line',
          location: {
            building: testData.location?.building || '',
            floor: testData.location?.floor || '',
            room: testData.location?.room || '',
            address: testData.location?.address || '',
            coordinates: {
              latitude: testData.location?.coordinates?.latitude || '',
              longitude: testData.location?.coordinates?.longitude || ''
            }
          },
          assignedTo: testData.assignedTo || ''
        });
        previousTestDataRef.current = testData;
      }
    }
  }, [testData, asset]);

  // Update form data when asset prop changes (only when asset ID changes, not on every render)
  useEffect(() => {
    const currentAssetId = asset?._id;

    // Only initialize if:
    // 1. We have an asset
    // 2. We're not using testData
    // 3. The asset ID has changed (different asset or first load)
    if (asset && !testData && currentAssetId && currentAssetId !== previousAssetIdRef.current) {
      let effectVendor = '';
      if (asset.vendor) {
        if (typeof asset.vendor === 'string') {
          effectVendor = asset.vendor === '[object Object]' ? '' : asset.vendor;
        } else if (typeof asset.vendor === 'object') {
          effectVendor = asset.vendor.name && asset.vendor.name !== '[object Object]' ? asset.vendor.name : '';
        }
      }

      console.log('AssetForm: Initializing form data with asset:', asset);
      const updatedFormData = {
        assetCode: asset.assetCode || '',
        name: asset.name || '',
        type: asset.type || '',
        subtype: asset.subtype || '',
        department: asset.department || '',
        status: asset.status || 'Active',
        purchaseDate: asset.purchaseDate ? new Date(asset.purchaseDate).toISOString().split('T')[0] : '',
        vendor: effectVendor,
        cost: asset.cost || '',
        depreciationMethod: asset.depreciation?.method || asset.depreciationMethod || 'Straight Line',
        location: {
          building: asset.location?.building || '',
          floor: asset.location?.floor || '',
          room: asset.location?.room || '',
          address: asset.location?.address || '',
          coordinates: {
            latitude: asset.location?.coordinates?.latitude || '',
            longitude: asset.location?.coordinates?.longitude || ''
          }
        },
        assignedTo: asset.assignedTo || ''
      };
      console.log('AssetForm: Setting form data to:', updatedFormData);
      setFormData(updatedFormData);
      previousAssetIdRef.current = currentAssetId;
    }
  }, [asset?._id, asset, testData]);

  const loadExistingDocuments = async () => {
    if (!asset?._id) return;

    try {
      setLoadingDocuments(true);
      const response = await assetService.getAssetDocuments(asset._id);
      if (response && response.documents) {
        setExistingDocuments(response.documents);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await assetService.deleteAssetDocument(asset._id, documentId);
      // Remove from state without reloading
      setExistingDocuments(prev => prev.filter(doc => doc._id !== documentId));
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle nested location fields
    if (name.startsWith('location.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev.location,
          [field]: value
        }
      }));
    } else if (name.startsWith('coordinates.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev.location,
          coordinates: {
            ...prev.location.coordinates,
            [field]: value
          }
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));

      // Load subtypes when asset type changes
      if (name === 'type') {
        loadSubtypes(value);
        // Clear subtype when type changes
        setFormData(prev => ({
          ...prev,
          subtype: ''
        }));
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Ensure vendor is a string before converting to object
      let vendorValue = formData.vendor;
      if (typeof vendorValue === 'object' && vendorValue !== null) {
        // If vendor is already an object, extract the name
        vendorValue = vendorValue.name || '';
      }

      // Ensure required fields are present with proper types
      const assetData = {
        ...formData,
        // Ensure required fields are set
        cost: formData.cost !== '' && formData.cost !== undefined ? parseFloat(formData.cost) : 0,
        currentValue: formData.cost !== '' && formData.cost !== undefined ? parseFloat(formData.cost) : 0, // Default to cost if not provided
        purchaseDate: formData.purchaseDate || new Date().toISOString().split('T')[0],
        // Convert vendor string to object format only if it's a valid string. If empty or missing, send null.
        vendor: vendorValue && typeof vendorValue === 'string' && vendorValue.trim() !== '' ? { name: vendorValue.trim() } : null,
        // Ensure status has a default value
        status: formData.status || 'Active'
      };

      // Save the asset first
      const savedAsset = await onSave(assetData);

      // If there's a file to upload and we have an asset ID
      if (uploadedFile && (asset?._id || savedAsset?._id)) {
        const assetId = asset?._id || savedAsset?._id;
        const uploadFormData = new FormData();
        uploadFormData.append('document', uploadedFile);

        await assetService.uploadDocument(assetId, uploadFormData);

        // Reload existing documents
        if (asset?._id) {
          loadExistingDocuments();
        }
      }
    } catch (error) {
      console.error('Error saving asset or uploading document:', error);
      throw error; // Re-throw to let parent handle the error
    }
  };

  // Default fallback data for master data
  const getDefaultMasterData = () => {
    return {
      types: [
        { code: 'REAL_ESTATE', label: 'Real Estate' },
        { code: 'VEHICLE', label: 'Vehicle' },
        { code: 'MACHINERY', label: 'Machinery' },
        { code: 'IT_EQUIPMENT', label: 'IT Equipment' },
        { code: 'FURNITURE', label: 'Furniture' },
        { code: 'LABORATORY_EQUIPMENT', label: 'Laboratory Equipment' },
        { code: 'MANUFACTURING_EQUIPMENT', label: 'Manufacturing Equipment' },
        { code: 'OTHER', label: 'Other' }
      ],
      subtypes: [],
      departments: [
        { code: 'IT', label: 'IT' },
        { code: 'HR', label: 'HR' },
        { code: 'Finance', label: 'Finance' },
        { code: 'Operations', label: 'Operations' },
        { code: 'Sales', label: 'Sales' },
        { code: 'Marketing', label: 'Marketing' },
        { code: 'Legal', label: 'Legal' },
        { code: 'Administration', label: 'Administration' },
        { code: 'Facilities', label: 'Facilities' },
        { code: 'Maintenance', label: 'Maintenance' },
        { code: 'Production', label: 'Production' },
        { code: 'Quality', label: 'Quality' },
        { code: 'Research', label: 'Research' },
        { code: 'Development', label: 'Development' },
        { code: 'Other', label: 'Other' }
      ],
      depreciationMethods: [
        { code: 'STRAIGHT_LINE', label: 'Straight Line' },
        { code: 'DECLINING_BALANCE', label: 'Declining Balance' },
        { code: 'SUM_OF_YEARS', label: 'Sum of Years' },
        { code: 'UNITS_OF_PRODUCTION', label: 'Units of Production' }
      ],
      statuses: [
        { code: 'ACTIVE', label: 'Active' },
        { code: 'INACTIVE', label: 'Inactive' },
        { code: 'UNDER_MAINTENANCE', label: 'Under Maintenance' },
        { code: 'DISPOSED', label: 'Disposed' },
        { code: 'RETIRED', label: 'Retired' },
        { code: 'LOST', label: 'Lost' },
        { code: 'DAMAGED', label: 'Damaged' },
        { code: 'RESERVED', label: 'Reserved' },
        { code: 'IN_USE', label: 'In Use' }
      ]
    };
  };

  // Load master data from API
  const loadMasterData = async () => {
    try {
      setLoadingMasterData(true);
      console.log('Loading master data from API...');
      const data = await AssetMasterDataService.getAllAssetMasterData();
      console.log('Master data loaded:', data);
      console.log('Types:', data.types);
      console.log('Departments:', data.departments);
      console.log('DepreciationMethods:', data.depreciationMethods);
      console.log('Statuses:', data.statuses);

      // Get default data as fallback
      const defaultData = getDefaultMasterData();

      // Use API data if available and not empty, otherwise use defaults
      const formattedData = {
        types: (Array.isArray(data.types) && data.types.length > 0) ? data.types : defaultData.types,
        subtypes: Array.isArray(data.subtypes) ? data.subtypes : [],
        departments: (Array.isArray(data.departments) && data.departments.length > 0) ? data.departments : defaultData.departments,
        depreciationMethods: (Array.isArray(data.depreciationMethods) && data.depreciationMethods.length > 0) ? data.depreciationMethods : defaultData.depreciationMethods,
        statuses: (Array.isArray(data.statuses) && data.statuses.length > 0) ? data.statuses : defaultData.statuses
      };

      console.log('Formatted master data (using defaults if empty):', formattedData);
      setMasterData(formattedData);
    } catch (error) {
      console.error('Error loading master data:', error);
      // Use default data if API fails
      const fallbackData = getDefaultMasterData();
      console.log('Using fallback master data:', fallbackData);
      setMasterData(fallbackData);
    } finally {
      setLoadingMasterData(false);
    }
  };

  // Load subtypes when asset type changes
  const loadSubtypes = async (assetType) => {
    console.log('Loading subtypes for asset type:', assetType);

    if (!assetType) {
      setMasterData(prev => ({ ...prev, subtypes: [] }));
      return;
    }

    setMasterData(prev => ({ ...prev, subtypes: [] }));

    const selectedType = masterData.types.find(type => type.label === assetType || type.code === assetType);
    const parentCandidates = Array.from(new Set([
      selectedType?.code,
      selectedType?.label,
      assetType
    ].filter(Boolean)));

    let fetchedSubtypes = [];
    let successfulParentValue = null;

    for (const parentValue of parentCandidates) {
      try {
        const response = await AssetMasterDataService.getAssetSubtypes(parentValue);
        console.log('Subtypes API response for parent value:', parentValue, response);
        if (response.data && response.data.length > 0) {
          fetchedSubtypes = response.data;
          successfulParentValue = parentValue;
          break;
        }
        console.log('No subtypes returned for parent value:', parentValue);
      } catch (error) {
        console.error('Error loading subtypes for parent value:', parentValue, error);
      }
    }

    if (fetchedSubtypes.length > 0) {
      console.log('Using subtypes fetched from API for parent value:', successfulParentValue);
      setMasterData(prev => ({ ...prev, subtypes: fetchedSubtypes }));
      return;
    }

    const hardcodedSubtypes = HARDCODED_SUBTYPES[assetType] || [];
    if (hardcodedSubtypes.length > 0) {
      console.log('Using hardcoded subtypes:', hardcodedSubtypes);
      setMasterData(prev => ({ ...prev, subtypes: hardcodedSubtypes }));
      return;
    }

    const fallbackType = assetTypes.find(type => type.name === assetType);
    const fallbackSubtypes = fallbackType ? fallbackType.subtypes.map(subtype => ({ code: subtype, label: subtype })) : [];
    console.log('Using fallback subtypes from dummyData:', fallbackSubtypes);
    setMasterData(prev => ({ ...prev, subtypes: fallbackSubtypes }));
  };

  const getSubtypes = () => {
    return masterData.subtypes || [];
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: '100%' }}>
      {/* Row 1: Asset Code, Asset Name, Category */}
      <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%' }}>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label required" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Asset Code <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              name="assetCode"
              {...getFormControlProps('text')}
              value={formData.assetCode}
              onChange={viewMode ? undefined : handleChange}
              required={!viewMode}
              placeholder="e.g., LAP-001"
            />
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label required" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Asset Name <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              name="name"
              {...getFormControlProps('text')}
              value={formData.name}
              onChange={viewMode ? undefined : handleChange}
              required={!viewMode}
              placeholder="Enter asset name"
            />
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label required" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Category <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <select
              name="type"
              {...getFormControlProps('select')}
              value={formData.type}
              onChange={viewMode ? undefined : handleChange}
              required={!viewMode}
            >
              <option value="">Select Category</option>
              {loadingMasterData ? (
                <option disabled>Loading...</option>
              ) : (
                masterData.types.map(type => (
                  <option key={type.code} value={type.label}>{type.label}</option>
                ))
              )}
            </select>
          </div>
        </div>
      </div>

      {/* Row 2: Subcategory, Department, Status */}
      <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%' }}>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className={`form-label ${getSubtypes().length > 0 ? 'required' : ''}`} style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Subcategory {getSubtypes().length > 0 && <span style={{ color: '#dc3545' }}>*</span>}
            </label>
            <select
              name="subtype"
              {...getFormControlProps('select')}
              value={formData.subtype}
              onChange={viewMode ? undefined : handleChange}
              required={!viewMode && getSubtypes().length > 0}
            >
              <option value="">Select Subcategory</option>
              {loadingMasterData ? (
                <option disabled>Loading...</option>
              ) : (
                getSubtypes().map(subtype => (
                  <option key={subtype.code} value={subtype.label}>{subtype.label}</option>
                ))
              )}
            </select>
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label required" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Department <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <select
              name="department"
              {...getFormControlProps('select')}
              value={formData.department}
              onChange={viewMode ? undefined : handleChange}
              required={!viewMode}
            >
              <option value="">Select Department</option>
              {loadingMasterData ? (
                <option disabled>Loading...</option>
              ) : (
                masterData.departments.map(dept => (
                  <option key={dept.code} value={dept.label}>{dept.label}</option>
                ))
              )}
            </select>
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Status
            </label>
            <select
              name="status"
              {...getFormControlProps('select')}
              value={formData.status}
              onChange={viewMode ? undefined : handleChange}
            >
              {loadingMasterData ? (
                <option disabled>Loading...</option>
              ) : (
                masterData.statuses.map(status => (
                  <option key={status.code} value={status.label}>{status.label}</option>
                ))
              )}
            </select>
          </div>
        </div>
      </div>

      {/* Row 3: Purchase Date, Vendor, Cost */}
      <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%' }}>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label required" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Purchase Date <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="date"
              name="purchaseDate"
              {...getFormControlProps('date')}
              value={formData.purchaseDate}
              onChange={viewMode ? undefined : handleChange}
              required={!viewMode}
            />
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Vendor
            </label>
            <input
              type="text"
              name="vendor"
              {...getFormControlProps('text')}
              value={formData.vendor}
              onChange={viewMode ? undefined : handleChange}
              placeholder="Enter vendor name"
            />
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label required" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Cost <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="number"
              name="cost"
              {...getFormControlProps('number')}
              value={formData.cost}
              onChange={viewMode ? undefined : handleChange}
              step="0.01"
              placeholder="0.00"
              min="0"
              required={!viewMode}
            />
          </div>
        </div>
      </div>

      {/* Row 4: Depreciation Method, Assigned To */}
      <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%' }}>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Depreciation Method
            </label>
            <select
              name="depreciationMethod"
              {...getFormControlProps('select')}
              value={formData.depreciationMethod}
              onChange={viewMode ? undefined : handleChange}
            >
              {loadingMasterData ? (
                <option disabled>Loading...</option>
              ) : (
                masterData.depreciationMethods.map(method => (
                  <option key={method.code} value={method.label}>{method.label}</option>
                ))
              )}
            </select>
          </div>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <div className="form-group" style={{ width: '100%' }}>
            <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
              Assigned To
            </label>
            <input
              type="text"
              name="assignedTo"
              {...getFormControlProps('text')}
              value={formData.assignedTo}
              onChange={viewMode ? undefined : handleChange}
              placeholder="Enter assigned person/department"
            />
          </div>
        </div>
      </div>

      {/* Location and Document Section - Side by Side */}
      <div className="form-row" style={{ display: 'flex', gap: '16px', width: '100%', marginBottom: '16px', alignItems: 'stretch' }}>
        {/* Location Section */}
        <div style={{
          flex: '1',
          backgroundColor: '#f8fafc',
          padding: '16px',
          borderRadius: '0',
          border: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <h6 style={{
            margin: '0 0 16px 0',
            color: '#374151',
            fontSize: '14px',
            fontWeight: '600',
            fontFamily: 'var(--font-family)'
          }}>
            📍 Location Details
          </h6>

          {/* Row 1: Building, Floor, Room */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', width: '100%' }}>
            <div style={{ flex: '1', minWidth: '0' }}>
              <div className="form-group" style={{ width: '100%' }}>
                <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
                  Building
                </label>
                <input
                  type="text"
                  name="location.building"
                  {...getFormControlProps('text')}
                  value={formData.location.building}
                  onChange={viewMode ? undefined : handleChange}
                  placeholder="Enter building name"
                />
              </div>
            </div>
            <div style={{ flex: '1', minWidth: '0' }}>
              <div className="form-group" style={{ width: '100%' }}>
                <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
                  Floor
                </label>
                <input
                  type="text"
                  name="location.floor"
                  {...getFormControlProps('text')}
                  value={formData.location.floor}
                  onChange={viewMode ? undefined : handleChange}
                  placeholder="Enter floor number"
                />
              </div>
            </div>
            <div style={{ flex: '1', minWidth: '0' }}>
              <div className="form-group" style={{ width: '100%' }}>
                <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
                  Room
                </label>
                <input
                  type="text"
                  name="location.room"
                  {...getFormControlProps('text')}
                  value={formData.location.room}
                  onChange={viewMode ? undefined : handleChange}
                  placeholder="Enter room number"
                />
              </div>
            </div>
          </div>

          {/* Row 2: Address */}
          <div style={{ marginBottom: '12px', width: '100%' }}>
            <div className="form-group" style={{ width: '100%' }}>
              <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
                Address
              </label>
              <textarea
                name="location.address"
                {...getFormControlProps('textarea')}
                rows="2"
                style={{
                  ...getFormControlProps('textarea').style,
                  resize: viewMode ? 'none' : 'vertical'
                }}
                value={formData.location.address}
                onChange={viewMode ? undefined : handleChange}
                placeholder="Enter full address"
              />
            </div>
          </div>

          {/* Row 3: Coordinates */}
          <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
            <div style={{ flex: '1', minWidth: '0' }}>
              <div className="form-group" style={{ width: '100%' }}>
                <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
                  Latitude
                </label>
                <input
                  type="number"
                  step="any"
                  name="coordinates.latitude"
                  {...getFormControlProps('number')}
                  value={formData.location.coordinates.latitude}
                  onChange={viewMode ? undefined : handleChange}
                  placeholder="Enter latitude"
                />
              </div>
            </div>
            <div style={{ flex: '1', minWidth: '0' }}>
              <div className="form-group" style={{ width: '100%' }}>
                <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
                  Longitude
                </label>
                <input
                  type="number"
                  step="any"
                  name="coordinates.longitude"
                  {...getFormControlProps('number')}
                  value={formData.location.coordinates.longitude}
                  onChange={viewMode ? undefined : handleChange}
                  placeholder="Enter longitude"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Document Upload Section */}
        <div style={{
          flex: '1',
          backgroundColor: '#f8fafc',
          padding: '16px',
          borderRadius: '0',
          border: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <label className="form-label" style={{
            fontWeight: '600',
            color: '#374151',
            marginBottom: '16px',
            fontSize: '14px',
            fontFamily: 'var(--font-family)'
          }}>
            Asset Specification Document
          </label>

          {/* Existing Documents */}
          {asset?._id && existingDocuments.length > 0 && (
            <div style={{ marginBottom: '12px', flex: '1', overflowY: 'auto' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>
                Existing Documents:
              </div>
              {existingDocuments.map((doc, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '0',
                  marginBottom: '4px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <FileText size={16} className="text-muted" />
                      <span style={{ fontSize: '13px', color: '#374151' }}>{doc.name || doc.filename || 'Document'}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {doc.url && (
                        <a
                          href={getFileUrl(doc.url)}
                          download={doc.name || 'document'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-light p-1"
                          title="Download"
                          style={{ color: '#4b5563', border: 'none', background: 'transparent' }}
                        >
                          <Download size={16} />
                        </a>
                      )}
                      {!viewMode && (
                        <button
                          type="button"
                          onClick={() => handleDeleteDocument(doc._id)}
                          className="btn btn-sm btn-light p-1"
                          title="Delete"
                          style={{ color: '#dc2626', border: 'none', background: 'transparent' }}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* New Document Upload */}
          {!viewMode && (
            <div style={{
              border: '2px dashed #d1d5db',
              borderRadius: '0',
              padding: '20px',
              textAlign: 'center',
              backgroundColor: '#f9fafb',
              flex: '1',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minHeight: '120px'
            }}>
              <input
                type="file"
                id="document-upload"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
              />
              <label
                htmlFor="document-upload"
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <div style={{ fontSize: '24px', color: '#6b7280' }}>📄</div>
                <div style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                  {uploadedFile ? uploadedFile.name : 'Click to upload new document'}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  PDF, DOC, XLS, TXT, Images (Max 10MB)
                </div>
              </label>
            </div>
          )}
        </div>
      </div>

      <div className="project-form-actions">
        {!viewMode && (
          <>
            <button
              type="button"
              className="btn btn-outline"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
            >
              {asset ? 'Update Asset' : 'Create Asset'}
            </button>
          </>
        )}
      </div>
    </form>
  );
};

export default AssetForm;
